// Preflight checks before spawn. Returns {ok:true} or {ok:false, reason,
// details} — never throws for expected-bad input (malformed settings.json,
// missing hook file), only for programmer errors (bad args, e.g. missing
// missionWorkspace/packRoot strings).
//
// Individual checks are also imported by autopilot-doctor.ts and
// mission-workspace.ts; `runSessionPreflightChecks` is the pre-spawn entry.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { pathExists } from "../filesystem-helpers.js";
import { AUTOPILOT_REQUIRED_HOOK_FILES } from "./mission-settings-profile.js";
import {
  checkDangerousGuardOptInFlag,
  checkRequiredAutopilotHooksRegistered,
} from "./session-preflight-guard-active-checks.js";
import { checkHookCommandsExist } from "./session-preflight-hook-file-checks.js";
import type { PreflightResult } from "./session-preflight-types.js";

// Split into session-preflight-{types,hook-file-checks,guard-active-checks}.ts
// (plan 20260924-0205); every name is re-exported so existing imports from
// "./session-preflight-checks.js" keep working.
export {
  checkDangerousGuardOptInFlag,
  checkRequiredAutopilotHooksRegistered,
} from "./session-preflight-guard-active-checks.js";
export { checkHookCommandsExist } from "./session-preflight-hook-file-checks.js";
export type { PreflightResult } from "./session-preflight-types.js";

export async function checkMissionSettingsParse(
  missionWorkspace: string,
): Promise<PreflightResult> {
  if (!missionWorkspace)
    throw new Error("checkMissionSettingsParse: missionWorkspace là bắt buộc.");

  const settingsPath = join(missionWorkspace, ".claude", "settings.json");
  if (!(await pathExists(settingsPath))) {
    return { ok: false, reason: "settings_missing", details: settingsPath };
  }
  let text: string;
  try {
    text = await fs.readFile(settingsPath, "utf8");
  } catch (err) {
    return {
      ok: false,
      reason: "settings_unreadable",
      details: err instanceof Error ? err.message : String(err),
    };
  }
  try {
    JSON.parse(text);
  } catch (err) {
    return {
      ok: false,
      reason: "settings_malformed_json",
      details: err instanceof Error ? err.message : String(err),
    };
  }
  return { ok: true };
}

export async function checkBindingHelperInPack(packRoot: string): Promise<PreflightResult> {
  if (!packRoot) throw new Error("checkBindingHelperInPack: packRoot là bắt buộc.");

  const helperPath = join(packRoot, "scripts", "autopilot_binding_common.py");
  if (!(await pathExists(helperPath))) {
    return { ok: false, reason: "binding_helper_missing", details: helperPath };
  }
  return { ok: true };
}

export async function runSessionPreflightChecks(
  missionWorkspace: string,
  packRoot: string,
): Promise<PreflightResult> {
  const settingsCheck = await checkMissionSettingsParse(missionWorkspace);
  if (!settingsCheck.ok) return settingsCheck;

  const hooksCheck = await checkHookCommandsExist(missionWorkspace);
  if (!hooksCheck.ok) return hooksCheck;

  // F4: hai check dưới là thứ `checkHookCommandsExist` KHÔNG BAO GIỜ hỏi được —
  // nó chỉ kiểm "hook đã khai có tồn tại file không", nên 0 hook = đạt. Đây mới
  // là "có hook nào đang canh thật không".
  const requiredHooksCheck = await checkRequiredAutopilotHooksRegistered(
    missionWorkspace,
    AUTOPILOT_REQUIRED_HOOK_FILES,
    packRoot,
  );
  if (!requiredHooksCheck.ok) return requiredHooksCheck;

  const guardFlagCheck = await checkDangerousGuardOptInFlag(missionWorkspace);
  if (!guardFlagCheck.ok) return guardFlagCheck;

  const helperCheck = await checkBindingHelperInPack(packRoot);
  if (!helperCheck.ok) return helperCheck;

  return { ok: true };
}
